import React, { useCallback, useState, useEffect, useMemo } from "react";
import GroupOperationPort from "../GroupOperations";
import ManagePortsService from "../../../services/managePorts.service";
import GroupOperationBandwidth from "../../ManageBandwidth/GroupOperations";
import Dropdown from "../../DesignSystem/dropdown_autocomplete";
import { NEW_GROUP_DROPDOWN } from "../../../utils/appConstants";
import { PORT_VIEW } from "../../PortDetails/portDetailsHook";
import { resolveTripleslashReference } from "typescript";
import { defaultValue } from "cesium";

// Expanded NE ID: ne id number

// Group Selected (NRs to add to group upon creation/change)
// FORMAT: [ {nrId: xxxx}, {...}]

export function useGroup(
  expandedNeId: string,
  options: {
    isMounted: boolean;
    isBandwidth: boolean;
    lowerDrawer: boolean;
  },
  drawerInputs: {
    groupedSelected: any[];
    allowAddToExisting: boolean;
    refetch: () => void;
  } | null,
  portDetails?: PORT_VIEW | null,
  addPortOptions?: {
    DefaultGroup: string | null;
  } | null
) {
  // ============ Existing Group Options, startup... =====================
  const [existingGroupOptions, setExistingGroupOptions] = useState<any[]>([]);
  const [debounceNewGroupName, setDebounceNewGroupName] = useState("");
  const [debounceOriginalGroupName, setDebounceOriginalGroupName] = useState("");
  const [portNamesInGroup, setPortNamesInGroup] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const DefaultGroup = useMemo(() => {
    if (!addPortOptions || !addPortOptions.DefaultGroup) return null;
    if (options.isBandwidth) return `> ${addPortOptions.DefaultGroup}`;
    else return `NE @ ${addPortOptions.DefaultGroup}`;
  }, [options.isBandwidth, addPortOptions?.DefaultGroup]);

  const onAddGroup = useCallback((group_name) => {
    setDebounceNewGroupName(group_name);
  }, []);
  const fetchGroups = useCallback(() => {
    if (!expandedNeId || expandedNeId === "undefined" || expandedNeId == undefined) return;
    setPortNamesInGroup([]);

    setLoading(true);
    ManagePortsService.getAllGroupsByNEid(expandedNeId, options.isBandwidth ? "bandwidth" : "port")
      .then((response: any) => {
        if (!options.isMounted) return;
        setLoading(false);
        if (response && response.data) {
          const existedGroups = response?.data.map((group: any) => {
            return {
              id: group.id,
              name: group.name,
            };
          });
          const groupName = existedGroups.find((grp: any) => grp.id === portDetails?.group_id);
          if (groupName) {
            setDebounceOriginalGroupName(groupName.name);
          }
          setExistingGroupOptions(existedGroups || []);
        }
      })
      .catch((err: any) => {
        if (!options.isMounted) return;
        setLoading(false);
        console.error(err);
      });
  }, [expandedNeId, options.isBandwidth, portDetails?.group_id]);
  useEffect(() => {
    if (expandedNeId) fetchGroups();
  }, [expandedNeId, portDetails?.group_id]);
  // Part of dropdown logic, but needed up here so that the drawer can use this function.

  // ============ G R O U P   D R A W E R   L O G I C ====================
  const [addGroupOpen, setAddGroupOpen] = useState<boolean>(false);
  const [editGroupOpen, setEditGroupOpen] = useState<boolean>(false);
  const [editGroupDetails, setEditGroupDetails] = useState<{
    name: string;
    description: string;
    icon: string;
    id: string;
  }>({
    name: "",
    description: "",
    icon: "",
    id: "",
  });
  const handleGroupClick = useCallback(() => {
    setAddGroupOpen(true);
  }, [setAddGroupOpen]);
  const handleEditGroupClick = useCallback(
    (group: any) => {
      setEditGroupOpen(true);
      setEditGroupDetails({
        name: group.groupName,
        description: group.description,
        icon: group.icon,
        id: group.groupId,
      });
    },
    [setEditGroupOpen, setEditGroupDetails]
  );
  const handleGroupClose = useCallback(
    (operation?: boolean) => {
      setEditGroupOpen(false);
      setAddGroupOpen(false);
      setEditGroupDetails({
        name: "",
        description: "",
        icon: "",
        id: "",
      });
      if (operation) {
        drawerInputs && drawerInputs.refetch();
        fetchGroups();
      }
    },
    [setEditGroupOpen, setEditGroupDetails, setAddGroupOpen, drawerInputs && drawerInputs.refetch, expandedNeId]
  );
  const GroupOperationMap = useMemo(() => {
    return {
      port: GroupOperationPort,
      bandwidth: GroupOperationBandwidth,
    };
  }, []);
  const GroupOperation = useMemo(() => {
    if (options.isBandwidth) return GroupOperationMap.bandwidth;
    return GroupOperationMap.port;
  }, [options.isBandwidth]);
  const GroupDrawer = useMemo(
    () => (
      <GroupOperation
        open={addGroupOpen || editGroupOpen}
        existingGroupOptions={existingGroupOptions}
        expandedNeId={expandedNeId}
        groupedSelected={(drawerInputs && drawerInputs.groupedSelected) || []}
        onClose={handleGroupClose}
        edit={editGroupOpen}
        groupId={editGroupDetails.id}
        name={editGroupDetails.name}
        description={editGroupDetails.description}
        icon={editGroupDetails.icon}
        allowAddToExisting={(drawerInputs && drawerInputs.allowAddToExisting) || false}
        onAddGroup={onAddGroup}
        lowerDrawer={options.lowerDrawer}
      />
    ),
    [
      addGroupOpen,
      editGroupOpen,
      existingGroupOptions,
      expandedNeId,
      drawerInputs && drawerInputs.groupedSelected,
      handleGroupClose,
      editGroupOpen,
      editGroupDetails.id,
      drawerInputs && drawerInputs.allowAddToExisting,
    ]
  );

  // ============ G R O U P   D R O P D O W N   L O G I C =================
  const [groupNameValue, setGroupNameValue] = useState("");
  const [groupNameInputValue, setGroupNameInputValue] = useState("");

  const handleGroupNameChange = useCallback(
    (value: any) => {
      if (value === NEW_GROUP_DROPDOWN) {
        handleGroupClick();
      } else {
        const group = existingGroupOptions.find((item) => item.name === value) || "";
        if (group) {
          setGroupNameValue(group.name);
          setGroupNameInputValue(group.name);
        } else if (DefaultGroup && value === DefaultGroup) {
          setGroupNameValue(DefaultGroup);
          setGroupNameInputValue(DefaultGroup);
          return;
        }
        if (addPortOptions) {
          const currGroup = existingGroupOptions.find((item) => item.name === group.name);
          if (currGroup) {
            const payload = {
              groupId: Number(currGroup.id),
              neId: Number(expandedNeId),
              isBandwidth: options.isBandwidth,
            };
            setLoading(true);
            ManagePortsService.getPortsInGroup(payload)
              .then((result: any) => {
                if (!options.isMounted) return;
                setLoading(false);
                if (result && result.data && Array.isArray(result.data)) {
                  setPortNamesInGroup(result.data);
                }
              })
              .catch(() => setLoading(false));
          } else {
            setPortNamesInGroup([]);
          }
        }
      }
    },
    [existingGroupOptions, DefaultGroup, expandedNeId, options.isBandwidth, addPortOptions]
  );
  // If we have a new group name, once the group list is updated search to fill the dropdown with this name
  useEffect(() => {
    if (debounceNewGroupName) {
      handleGroupNameChange(debounceNewGroupName);
      setDebounceNewGroupName("");
      setDebounceOriginalGroupName("");
    } else if (debounceOriginalGroupName) {
      handleGroupNameChange(debounceOriginalGroupName);
      setDebounceOriginalGroupName("");
    }
  }, [existingGroupOptions]);
  useEffect(() => {
    if (DefaultGroup && expandedNeId) {
      handleGroupNameChange(DefaultGroup);
    }
  }, [expandedNeId, DefaultGroup, existingGroupOptions]);
  const handleGroupNameInputChange = useCallback((inputValue: string) => {
    setGroupNameInputValue(inputValue);
  }, []);

  const GroupNameDropdown = useMemo(() => {
    let className = "";
    const disabled = !expandedNeId || (!DefaultGroup && !!addPortOptions);
    if (disabled) className += " disabled";
    if (options.isBandwidth || options.lowerDrawer) className += " bandwidth";
    if (options.lowerDrawer) className += " shrink-height";
    const dropdownOptions = [NEW_GROUP_DROPDOWN];
    if (DefaultGroup && existingGroupOptions.findIndex((item) => item.name === DefaultGroup) === -1) dropdownOptions.push(DefaultGroup);
    dropdownOptions.push(...existingGroupOptions.map((group: any) => group.name));

    return (
      <Dropdown
        value={groupNameValue}
        onInputChange={handleGroupNameInputChange}
        onChange={handleGroupNameChange}
        inputValue={groupNameInputValue}
        options={dropdownOptions}
        disabled={disabled}
        className={className}
      />
    );
  }, [
    existingGroupOptions,
    handleGroupClick,
    expandedNeId,
    groupNameValue,
    groupNameInputValue,
    handleGroupNameChange,
    handleGroupNameInputChange,
    onAddGroup,
    options.isBandwidth,
    DefaultGroup,
  ]);

  const IsPortNameValid = useCallback(
    (portName: string) => {
      return !portNamesInGroup.find((item: string) => portName === item);
    },
    [portNamesInGroup, DefaultGroup]
  );

  const currentGroup = useMemo(() => {
    const group = existingGroupOptions.find((item) => item.name === groupNameValue);
    if (group) return group;
    else if (groupNameValue === DefaultGroup) return { name: DefaultGroup, id: -1 };
    else return undefined;
  }, [groupNameValue, groupNameValue, existingGroupOptions]);

  // Exposed Parts Of Hook
  return {
    GroupDrawerProps: {
      drawerComponent: GroupDrawer,
      addGroup: handleGroupClick,
      editGroup: handleEditGroupClick,
    },
    GroupDropdownProps: {
      dropdownComponent: GroupNameDropdown,
      groupName: currentGroup ? currentGroup.name : "",
      groupId: currentGroup ? currentGroup.id : -1,
      reset: (groupId: number | null = null) => {
        if (groupId) {
          const group = existingGroupOptions.find((grp: any) => grp.id === groupId);
          if (group) {
            setGroupNameValue(group.name);
            setGroupNameInputValue(group.name);
          } else {
            setGroupNameInputValue("");
            setGroupNameValue("");
          }
        } else {
          setGroupNameInputValue("");
          setGroupNameValue("");
        }
      },
      IsPortNameValid,
      loading: loading || !!debounceNewGroupName || !!debounceOriginalGroupName,
    },
  };
}
